import React from 'react'

interface InvoiceViewProps {
  onClose: () => void
  primaryColor?: string
  invoice?: {
    id: string
    date: string
    amount: number
    currency: string
    items: Array<{
      name: string
      quantity: number
      price: number
    }>
    customerName?: string
    customerEmail?: string
  }
}

export function InvoiceView({ onClose, primaryColor = '#3b82f6', invoice }: InvoiceViewProps) {
  const mockInvoice = invoice || {
    id: 'INV-2024-001',
    date: new Date().toLocaleDateString(),
    amount: 150.00,
    currency: 'GHS',
    items: [
      { name: 'Product/Service', quantity: 1, price: 150.00 }
    ],
    customerName: 'Customer',
    customerEmail: 'customer@example.com'
  }

  const handleDownload = () => {
    // Mock download functionality
    alert('Invoice download started...')
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h4 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Invoice</h4>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px'
          }}
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div style={{
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '20px',
        backgroundColor: 'white'
      }}>
        {/* Invoice Header */}
        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
          <h3 style={{ fontSize: '24px', fontWeight: 'bold', color: primaryColor, margin: '0 0 8px 0' }}>
            INVOICE
          </h3>
          <p style={{ margin: 0, color: '#6b7280' }}>#{mockInvoice.id}</p>
        </div>

        {/* Invoice Details */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
          <div>
            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>Bill To:</h4>
            <p style={{ margin: '0 0 4px 0', fontWeight: '600' }}>{mockInvoice.customerName}</p>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>{mockInvoice.customerEmail}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>Invoice Date:</h4>
            <p style={{ margin: 0, fontSize: '14px' }}>{mockInvoice.date}</p>
          </div>
        </div>

        {/* Items Table */}
        <div style={{ marginBottom: '24px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '12px 0', fontSize: '14px', fontWeight: '600' }}>Item</th>
                <th style={{ textAlign: 'center', padding: '12px 0', fontSize: '14px', fontWeight: '600' }}>Qty</th>
                <th style={{ textAlign: 'right', padding: '12px 0', fontSize: '14px', fontWeight: '600' }}>Price</th>
              </tr>
            </thead>
            <tbody>
              {mockInvoice.items.map((item, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '12px 0', fontSize: '14px' }}>{item.name}</td>
                  <td style={{ textAlign: 'center', padding: '12px 0', fontSize: '14px' }}>{item.quantity}</td>
                  <td style={{ textAlign: 'right', padding: '12px 0', fontSize: '14px' }}>
                    {mockInvoice.currency} {item.price.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Total */}
        <div style={{ textAlign: 'right', marginBottom: '24px' }}>
          <div style={{ 
            display: 'inline-block',
            padding: '16px',
            backgroundColor: `${primaryColor}10`,
            borderRadius: '8px',
            border: `1px solid ${primaryColor}30`
          }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: primaryColor }}>
              Total: {mockInvoice.currency} {mockInvoice.amount.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            onClick={handleDownload}
            style={{
              padding: '10px 20px',
              backgroundColor: primaryColor,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download PDF
          </button>
          <button
            onClick={handlePrint}
            style={{
              padding: '10px 20px',
              backgroundColor: 'white',
              color: primaryColor,
              border: `1px solid ${primaryColor}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
        </div>
      </div>
    </div>
  )
}